import { EventType, IncrementalSource, MouseInteractions } from '@twipla/recordings-player';
import {
  AbeIcon,
  BorderIcon,
  ClickIcon,
  FallbackIconCircle,
  InputIcon,
  MouseScrollIcon,
  NavEcommerce,
  PendingIcon,
  ProcessIcon,
  RefreshIcon,
  TapIcon,
} from '@va/icons';
import { AlarmingEventTrigger, WixStoreEventTrigger, WixStoreEventType } from '@va/types/events';
import { AlarmingEventType, AlarmingEventTypeEnum, RegularEventTypeEnum } from '@va/types/recordings';

type SSREvent = {
  type: EventType;
  data: {
    href?: string;
    source?: number | string;
    type?: number;
  };
};

export function getEventIcon({ eventDetails, color }: { eventDetails: string; color?: string }): {
  backgroundColor?: string;
  icon: (className?: string) => JSX.Element;
} {
  switch (eventDetails) {
    case RegularEventTypeEnum.mouseDown:
    case RegularEventTypeEnum.mouseUp:
    case RegularEventTypeEnum.mouseMove:
    case RegularEventTypeEnum.click:
    case RegularEventTypeEnum.dblClick:
      return { icon: (className) => <ClickIcon color={color ?? '#0C635A'} className={className} /> };
    case RegularEventTypeEnum.touchStart:
    case RegularEventTypeEnum.touchEnd:
      return { icon: (className) => <TapIcon color={color ?? '#0C635A'} className={className} /> };
    case RegularEventTypeEnum.scroll:
      return { icon: (className) => <MouseScrollIcon color={color ?? '#F66F1E'} className={className} /> };
    case RegularEventTypeEnum.navigateToOtherPage:
    case RegularEventTypeEnum.entryPageVisited:
      return { icon: (className) => <ProcessIcon color={color ?? '#A50FEC'} className={className} /> };
    case RegularEventTypeEnum.focus:
      return { icon: (className) => <PendingIcon color={color ?? '#2478F0'} className={className} /> };
    case RegularEventTypeEnum.viewportResize:
      return { icon: (className) => <BorderIcon color={color ?? '#ED3FA5'} className={className} /> };
    case RegularEventTypeEnum.input:
      return { icon: (className) => <InputIcon color={color ?? '#2478F0'} className={className} /> };
    case RegularEventTypeEnum.pageRefresh:
      return { icon: (className) => <RefreshIcon color={color ?? '#4A90E2'} className={className} /> };
    case AlarmingEventTypeEnum.rageClick:
    case AlarmingEventTypeEnum.rageClickStart:
    case AlarmingEventTypeEnum.rageClickEnd:
    case AlarmingEventTypeEnum.mouseMovesStart:
    case AlarmingEventTypeEnum.mouseMovesEnd:
    case AlarmingEventTypeEnum.mouseMoveAbe:
    case AlarmingEventTypeEnum.deadClick:
    case AlarmingEventTypeEnum.scrollStart:
    case AlarmingEventTypeEnum.scrollEnd:
    case AlarmingEventTypeEnum.scrollAbe:
    case AlarmingEventTypeEnum.uTurn:
    case AlarmingEventTypeEnum.uTurnStart:
    case AlarmingEventTypeEnum.uTurnEnd:
    case AlarmingEventTypeEnum.refresh:
    case AlarmingEventTypeEnum.refreshStart:
    case AlarmingEventTypeEnum.refreshEnd:
      return {
        backgroundColor: '#EA2A0C',
        icon: (className) => <AbeIcon color={color ?? '#fff'} className={className} />,
      };
    case WixStoreEventType.AddToCart:
    case WixStoreEventType.AddProductImpression:
    case WixStoreEventType.ProductPageLoaded:
    case WixStoreEventType.StartPayment:
    case WixStoreEventType.AddDeliveryMethod:
    case WixStoreEventType.AddShippingDetails:
    case WixStoreEventType.AgreeToTerms:
    case WixStoreEventType.PickupDetails:
    case WixStoreEventType.ClickProduct:
    case WixStoreEventType.RemoveFromCart:
    case WixStoreEventType.InitiateCheckout:
    case WixStoreEventType.AddPaymentInfo:
    case WixStoreEventType.Purchase:
    case WixStoreEventType.ViewContent:
    case WixStoreEventType.CustomizeProduct:
      return {
        icon: (className) => <NavEcommerce color={color ?? '#09AF5C'} className={className} />,
      };
    default:
      return { icon: (className) => <FallbackIconCircle className={className} /> };
  }
}

export function computeCustomEventDetails(event: SSREvent) {
  if (event.type !== EventType.Custom) return undefined;
  const details = computeEventDetails(event);

  if (!('name' in details) || details.name === undefined) return undefined;
  return details;
}

export function computeEventDetails(event: SSREvent) {
  switch (event.type) {
    case EventType.Meta:
      return {
        color: '#4A90E2',
        details: `Navigate to ${event.data.href}`,
        name: 'meta',
        extras: { url: event.data.href },
      };
    case EventType.FullSnapshot:
      return {
        color: '#F492F7',
        details: 'Full Snapshot',
        name: 'fullSnapshot',
      };
    case EventType.IncrementalSnapshot: {
      switch (event.data.source) {
        case IncrementalSource.MouseMove:
          return {
            color: '#3A505A',
            details: 'Mouse moved',
            name: 'mouseMove',
          };
        case IncrementalSource.Input:
          return {
            color: '#F6EC67',
            details: 'Input field change',
            name: 'input',
          };
        case IncrementalSource.Mutation:
          return { color: '#4A90E2', details: 'Mutation', name: 'mutation' };
        case IncrementalSource.Scroll:
          return { color: '#016D62', details: 'Scrolling', name: 'scroll' };
        case IncrementalSource.ViewportResize:
          return {
            color: '#73DEF6',
            details: 'Viewport resize',
            name: 'viewportResize',
          };
        case IncrementalSource.MouseInteraction: {
          switch (event.data.type) {
            case MouseInteractions.MouseUp:
              return { color: '#FDC96B', details: 'Mouse up', name: 'mouseUp' };
            case MouseInteractions.MouseDown:
              return {
                color: '#F2B44F',
                details: 'Mouse down',
                name: 'mouseDown',
              };
            case MouseInteractions.Click:
              return { color: '#FF5252', details: 'Click', name: 'click' };
            case MouseInteractions.ContextMenu:
              return {
                color: '#455773',
                details: 'Context menu',
                name: 'contextMenu',
              };
            case MouseInteractions.DblClick:
              return {
                color: '#C23853',
                details: 'Double click',
                name: 'dblClick',
              };
            case MouseInteractions.Focus:
              return { color: '#F27F4F', details: 'Focus', name: 'focus' };
            case MouseInteractions.Blur:
              return { color: '#49C5B6', details: 'Blur', name: 'blur' };
            case MouseInteractions.TouchStart:
              return {
                color: '#F492F7',
                details: 'Touch start',
                name: 'touchStart',
              };
            case MouseInteractions.TouchMove_Departed:
              return {
                color: '#8F7CED',
                details: 'Touch move',
                name: 'touchMove',
              };
            case MouseInteractions.TouchEnd:
              return {
                color: '#8F7CED',
                details: 'Touch end',
                name: 'touchEnd',
              };
            default:
              return {
                color: '#FDC96B',
                details: `${MouseInteractions[event.data.type as unknown as keyof typeof MouseInteractions]}`,
              };
          }
        }
        default:
          return {
            color: '#55BFA0',
            details: `${IncrementalSource[event.data.source as keyof typeof IncrementalSource]}`,
          };
      }
    }
    case EventType.Custom: {
      switch (event.data.source) {
        case AlarmingEventTrigger.rageClicks: {
          return {
            color: '#FF0000',
            details: event.data.type,
            name:
              event.data.type === AlarmingEventType.ABE_START
                ? AlarmingEventTypeEnum.rageClickStart
                : AlarmingEventTypeEnum.rageClickEnd,
            height: '16px',
          };
        }
        case AlarmingEventTrigger.deadClicks: {
          return {
            color: '#FF0000',
            details: event.data.type,
            name: AlarmingEventTypeEnum.deadClick,
            height: '16px',
          };
        }
        case AlarmingEventTrigger.scrolls: {
          return {
            color: '#FF0000',
            details: event.data.type,
            name:
              event.data.type === AlarmingEventType.ABE_START
                ? AlarmingEventTypeEnum.scrollStart
                : AlarmingEventTypeEnum.scrollEnd,
            height: '16px',
          };
        }
        case AlarmingEventTrigger.mouseMoves: {
          return {
            color: '#FF0000',
            details: event.data.type,
            name:
              event.data.type === AlarmingEventType.ABE_START
                ? AlarmingEventTypeEnum.mouseMovesStart
                : AlarmingEventTypeEnum.mouseMovesEnd,
            height: '16px',
          };
        }
        case AlarmingEventTrigger.uTurns: {
          return {
            color: '#FF0000',
            details: event.data.type,
            name:
              event.data.type === AlarmingEventType.ABE_START
                ? AlarmingEventTypeEnum.uTurnStart
                : AlarmingEventTypeEnum.uTurnEnd,
            height: '16px',
          };
        }
        case AlarmingEventTrigger.refreshes: {
          return {
            color: '#FF0000',
            details: event.data.type,
            name:
              event.data.type === AlarmingEventType.ABE_START
                ? AlarmingEventTypeEnum.refreshStart
                : AlarmingEventTypeEnum.refreshEnd,
            height: '16px',
          };
        }

        case WixStoreEventTrigger.AddToCart:
        case WixStoreEventTrigger.AddProductImpression:
        case WixStoreEventTrigger.ProductPageLoaded:
        case WixStoreEventTrigger.StartPayment:
        case WixStoreEventTrigger.AddDeliveryMethod:
        case WixStoreEventTrigger.AddShippingDetails:
        case WixStoreEventTrigger.AgreeToTerms:
        case WixStoreEventTrigger.PickupDetails:
        case WixStoreEventTrigger.ClickProduct:
        case WixStoreEventTrigger.RemoveFromCart:
        case WixStoreEventTrigger.InitiateCheckout:
        case WixStoreEventTrigger.AddPaymentInfo:
        case WixStoreEventTrigger.Purchase:
        case WixStoreEventTrigger.ViewContent:
        case WixStoreEventTrigger.CustomizeProduct:
          return {
            color: '#09AF5C',
            details: event.data.type,
            name: event.data.type,
            height: '16px',
          };

        default: {
          return { color: '#FDC96B', details: event.data.source };
        }
      }
    }

    default:
      return { color: '#FDC96B', details: `${EventType[event.type]}` };
  }
}

export const EVENTS_COMPACT_CONFIGURATION = [
  {
    startEvent: AlarmingEventTypeEnum.rageClickStart,
    endEvent: AlarmingEventTypeEnum.rageClickEnd,
    namesToRemove: [
      RegularEventTypeEnum.click,
      RegularEventTypeEnum.dblClick,
      RegularEventTypeEnum.mouseDown,
      RegularEventTypeEnum.mouseUp,
      RegularEventTypeEnum.focus,
    ],
  },
  {
    startEvent: AlarmingEventTypeEnum.scrollStart,
    endEvent: AlarmingEventTypeEnum.scrollEnd,
    namesToRemove: [RegularEventTypeEnum.scroll],
  },
  {
    startEvent: AlarmingEventTypeEnum.mouseMovesStart,
    endEvent: AlarmingEventTypeEnum.mouseMovesEnd,
    namesToRemove: [RegularEventTypeEnum.mouseMove],
  },
  {
    startEvent: AlarmingEventTypeEnum.uTurnStart,
    endEvent: AlarmingEventTypeEnum.uTurnEnd,
    namesToRemove: [],
  },
  {
    startEvent: AlarmingEventTypeEnum.refreshStart,
    endEvent: AlarmingEventTypeEnum.refreshEnd,
    namesToRemove: [],
  },
];

export const EVENTS_MERGE_CONFIGURATION: Record<
  string,
  { eventType: string; eventTypeAfterMerge: string; maxDelayDifference?: number }
> = {
  [RegularEventTypeEnum.viewportResize]: {
    eventType: RegularEventTypeEnum.viewportResize,
    maxDelayDifference: 1000,
    eventTypeAfterMerge: RegularEventTypeEnum.viewportResize,
  },
  [RegularEventTypeEnum.mouseMove]: {
    eventType: RegularEventTypeEnum.mouseMove,
    maxDelayDifference: 1000,
    eventTypeAfterMerge: RegularEventTypeEnum.mouseMove,
  },
  [RegularEventTypeEnum.input]: {
    eventType: RegularEventTypeEnum.input,
    maxDelayDifference: 1000,
    eventTypeAfterMerge: RegularEventTypeEnum.input,
  },
  [RegularEventTypeEnum.scroll]: {
    eventType: RegularEventTypeEnum.scroll,
    maxDelayDifference: 1000,
    eventTypeAfterMerge: RegularEventTypeEnum.scroll,
  },
  [AlarmingEventTypeEnum.rageClickStart]: {
    eventType: AlarmingEventTypeEnum.rageClickEnd,
    eventTypeAfterMerge: AlarmingEventTypeEnum.rageClick,
  },
  [AlarmingEventTypeEnum.mouseMovesStart]: {
    eventType: AlarmingEventTypeEnum.mouseMovesEnd,
    eventTypeAfterMerge: AlarmingEventTypeEnum.mouseMoveAbe,
  },
  [AlarmingEventTypeEnum.scrollStart]: {
    eventType: AlarmingEventTypeEnum.scrollEnd,
    eventTypeAfterMerge: AlarmingEventTypeEnum.scrollAbe,
  },
  [AlarmingEventTypeEnum.refreshStart]: {
    eventType: AlarmingEventTypeEnum.refreshEnd,
    eventTypeAfterMerge: AlarmingEventTypeEnum.refresh,
  },
  [AlarmingEventTypeEnum.uTurnStart]: {
    eventType: AlarmingEventTypeEnum.uTurnEnd,
    eventTypeAfterMerge: AlarmingEventTypeEnum.uTurn,
  },
};
